import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { getUserOrgId } from "../getUserOrgId";
import {
  useMutation,
  useQueries,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import getOrganisationUsers, {
  OrganisationUsersResponse,
} from "../http/getOrganisationUsers";
import postOrganisationUser, {
  OrganisationUserPayload,
} from "../http/postOrganisationUser";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const useOrgListUsers = (
  orgIds: string[],
  siteId?: string,
  userOrgId?: string
) => {
  const queryClient = useQueryClient();
  const { getToken, getClaim } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const orgListUsers: UseQueryResult<OrganisationUsersResponse, unknown>[] =
    useQueries({
      queries: orgIds.map((orgId) => ({
        queryKey: ["org", orgId, "users"],
        queryFn: async (): Promise<OrganisationUsersResponse> => {
          if (!orgId) {
            return { users: [] };
          }

          return getOrganisationUsers(
            await getAuthTokenBlocking(getToken, relogin),
            orgId,
            siteId,
            await getUserOrgId(getClaim, userOrgId)
          );
        },
      })),
    });

  const addOrganisationUser = useMutation(
    async (userData: { orgId: string } & OrganisationUserPayload) => {
      const { orgId, ...data } = userData;
      return postOrganisationUser(
        await getAuthTokenBlocking(getToken, relogin),
        orgId,
        data,
        await getUserOrgId(getClaim, userOrgId)
      );
    },
    {
      onSuccess: (_, userData: { orgId: string } & OrganisationUserPayload) => {
        const { orgId } = userData;
        queryClient.invalidateQueries(["org", orgId]);
      },
    }
  );

  return {
    orgListUsers,
    addOrganisationUser,
  };
};

export default useOrgListUsers;
